/* oxlint-disable jsx-a11y/no-noninteractive-tabindex */
// The line up above is to take care of the tabIndex prop in render, but I can't embed
// it in the middle of the prop list, or else it causes a parse error, and I can't use
// disable-next-line unless I do, so for now, we'll disable this rule for the whole module.

import React, { useCallback, useContext, useEffect } from 'react';

import { Hotkey } from '../hotkey';
import { HIGH_PRIORITY_QUEUE, LOW_PRIORITY_QUEUE } from './constants';
import { Context, listenerTypes, Provider as GlobalEventListenerProvider } from './context';
// @ts-expect-error less import
import style from './root.module.less';
import { Provider as HotkeyProvider } from '../hotkey/context';
import { useGlobalHotkeys } from '../hotkey/hooks';

/**
 * @typedef {import('./global-event-listener-types.d.ts').GlobalEventListenerRootComponent} GlobalEventListenerRootComponent
 * @typedef {import('./global-event-listener-types.d.ts').GlobalEventListenerRootContextWrapperComponent} GlobalEventListenerRootContextWrapperComponent
 * @typedef {import('./global-event-listener-types.d.ts').ListenerTypes} ListenerTypes
 */

/**
 * @type {GlobalEventListenerRootComponent}
 */
const GlobalEventListenerRootComponent = React.forwardRef(({ children, onBlur, onFocus }, ref) => {
  const { listeners } = useContext(Context);

  useEffect(() => {
    const onResize = (event, ...args) => {
      [HIGH_PRIORITY_QUEUE, LOW_PRIORITY_QUEUE].forEach((queue) => {
        if (listeners.current.onResize[queue].length === 0) {
          return;
        }

        let isPropagationStopped = false;

        // oxlint-disable-next-line no-param-reassign
        event.stopPropagation = () => {
          isPropagationStopped = true;
        };

        // oxlint-disable-next-line no-param-reassign
        event.isPropagationStopped = () => isPropagationStopped;

        listeners.current.onResize[queue].forEach((eventHandler) => {
          if (!event.isPropagationStopped()) {
            eventHandler(event, ...args);
          }
        });
      });
    };

    const onScroll = (event, ...args) => {
      [HIGH_PRIORITY_QUEUE, LOW_PRIORITY_QUEUE].forEach((queue) => {
        if (listeners.current.onScroll[queue].length === 0) {
          return;
        }

        let isPropagationStopped = false;

        // oxlint-disable-next-line no-param-reassign
        event.stopPropagation = () => {
          isPropagationStopped = true;
        };

        // oxlint-disable-next-line no-param-reassign
        event.isPropagationStopped = () => isPropagationStopped;

        listeners.current.onScroll[queue].forEach((eventHandler) => {
          if (!event.isPropagationStopped()) {
            eventHandler(event, ...args);
          }
        });
      });
    };

    global.window.addEventListener('resize', onResize);
    // For global scroll events, have to listen in capture vs bubbling mode
    global.window.addEventListener('scroll', onScroll, true);

    return () => {
      global.window.removeEventListener('resize', onResize);
      global.window.removeEventListener('scroll', onScroll, true);
    };
  }, [listeners]);

  const calculatedListeners = listenerTypes.reduce((eventListeners, eventType) => {
    //  TODO: This looks kinda fishy and breaks some hooks rules.
    // oxlint-disable-next-line react-hooks/rules-of-hooks, no-param-reassign
    eventListeners[eventType] = useCallback(
      (event, ...args) => {
        [HIGH_PRIORITY_QUEUE, LOW_PRIORITY_QUEUE].forEach((queue) => {
          listeners.current[eventType][queue].forEach((eventHandler) => {
            if (!event.isPropagationStopped()) {
              eventHandler(event, ...args);
            }
          });
        });

        // There are cases where our global event listeners swallow events that
        // we need to handle, adding the 'data-ignore-root-event-listeners' attribute
        // gives us an escape hatch to allow events to bubble up past this component.
        if (!event.target.closest('[data-ignore-root-event-listeners]')) {
          event.stopPropagation();
        }
      },
      [eventType],
    );

    return eventListeners;
  }, /** @type {Record<ListenerTypes, React.EventHandler<any>>} */ ({}));

  delete calculatedListeners.onResize;
  delete calculatedListeners.onScroll;

  return (
    <div ref={ref} className={style['global-event-listener-root']} {...calculatedListeners} onBlur={onBlur} onFocus={onFocus} tabIndex={0}>
      {children}
    </div>
  );
});

/**
 * @type {GlobalEventListenerRootContextWrapperComponent}
 */
export const GlobalEventListenerRoot = React.forwardRef(({ children }, ref) => {
  const globalHotkeys = useGlobalHotkeys();

  // These global hotkeys will be mounted after our view's children, so that
  // any hotkeys in the component tree of 'children' will be registered prior
  // to global hotkeys that are bleeding through in this layer. That way, we
  // can allow deeper hotkeys to override or squash more ancestral hotkeys,
  // if we have the need. An example of this is ScanToView.
  const reRegisteredHotkeys = globalHotkeys.map((hotkey, index) => <Hotkey key={index} hotkey={hotkey} />);

  return (
    <GlobalEventListenerProvider ref={ref}>
      <GlobalEventListenerRootComponent>
        <HotkeyProvider>
          <React.Fragment>
            {children}
            {reRegisteredHotkeys}
          </React.Fragment>
        </HotkeyProvider>
      </GlobalEventListenerRootComponent>
    </GlobalEventListenerProvider>
  );
});
